// useFocusItem — scrolls to and highlights an item referenced via ?focus=<id>
// in the URL query string. Used by both FamilyPortal and AdviserPortal so deep
// links from emails ("Open in portal" buttons) land on the exact row.
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Reads `?focus=<id>` from the URL once data is ready, then scrolls to and
 * temporarily highlights the matching <element id="portal-item-<id>">.
 *
 * Pass `ready` so the effect waits until your portal data has loaded into the
 * DOM (otherwise the element doesn't exist when we try to scroll).
 */
export function useFocusItem(ready) {
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (!ready) return;
    const focusId = params.get('focus');
    if (!focusId) return;
    // Defer one frame so React has finished painting
    const tid = setTimeout(() => {
      const el = document.getElementById(`portal-item-${focusId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('bg-[#FFF8F0]', 'ring-2', 'ring-[#A86A3A]/40');
        setTimeout(() => {
          el.classList.remove('bg-[#FFF8F0]', 'ring-2', 'ring-[#A86A3A]/40');
        }, 3500);
      }
      // Always clean the URL so refresh doesn't re-trigger — even when the
      // referenced item isn't on the page (e.g. user navigated elsewhere).
      params.delete('focus');
      setParams(params, { replace: true });
    }, 250);
    return () => clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, params]);
}
